import { Button, Card, CardActions, CardContent, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimelineIcon from '@mui/icons-material/Timeline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const TRADING_VIEW_CHART_URL = 'https://www.tradingview.com/chart/';

function openTradingView(symbol) {
  const tradingViewSymbol = `BINANCE:${String(symbol || '').toUpperCase()}.P`;
  const url = `${TRADING_VIEW_CHART_URL}?symbol=${encodeURIComponent(tradingViewSymbol)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function formatPrice(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Đang cập nhật';
  return number.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

const SOURCE_STYLES = {
  DEGO: { label: 'DEGO', color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.16)' },
  RSI: { label: 'RSI', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.16)' },
};

export default function SymbolCard({ item, busy, onOrder, onUnwatch, onKronosCheck, kronosBusy, showKronos }) {
  const side = String(item.watch_side || item.watchSide || '').toUpperCase();
  const isLong = side === 'LONG';
  const isShort = side === 'SHORT';
  const hasSide = isLong || isShort;
  const canOrder = hasSide;
  const accent = isLong ? '#4caf50' : '#f44336';
  const softAccent = isLong ? 'rgba(76, 175, 80, 0.14)' : 'rgba(244, 67, 54, 0.14)';
  const SideIcon = isLong ? TrendingUpIcon : TrendingDownIcon;
  const source = String(item.source || '').toUpperCase();
  const sourceStyle = SOURCE_STYLES[source];

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 150,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid',
        borderLeft: '6px solid',
        borderColor: 'rgba(255,255,255,0.06)',
        borderLeftColor: hasSide ? accent : 'grey.600',
        borderRadius: 3,
        backgroundColor: '#252525',
        transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          borderColor: 'rgba(255,255,255,0.12)',
          borderLeftColor: hasSide ? accent : 'grey.600',
          boxShadow: `0 12px 30px ${isLong ? 'rgba(76,175,80,.12)' : 'rgba(244,67,54,.12)'}`,
        },
      }}
    >
      <CardContent sx={{ p: 1.6, pb: 1, flexGrow: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
          <BoxTitle symbol={item.symbol} />
          <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
            {sourceStyle ? (
              <Chip
                label={sourceStyle.label}
                sx={{
                  color: sourceStyle.color,
                  bgcolor: sourceStyle.bg,
                  border: '1px solid',
                  borderColor: `${sourceStyle.color}66`,
                  height: 23,
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  letterSpacing: 0.6,
                }}
              />
            ) : null}
            {hasSide ? (
              <Chip
                icon={<SideIcon />}
                label={side}
                sx={{
                  color: accent,
                  bgcolor: softAccent,
                  border: '1px solid',
                  borderColor: `${accent}66`,
                  height: 23,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  '& .MuiChip-icon': { color: accent },
                }}
              />
            ) : null}
            {showKronos ? (
              <Tooltip title="Check nhanh bằng Kronos">
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    disabled={busy || kronosBusy}
                    onClick={() => onKronosCheck?.(item)}
                    aria-label={`Check Kronos ${item.symbol}`}
                    sx={{ width: 28, height: 28 }}
                  >
                    {kronosBusy ? <CircularProgress size={15} color="inherit" /> : <TimelineIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <Tooltip title="Mở trên TradingView">
              <IconButton size="small" onClick={() => openTradingView(item.symbol)} aria-label={`Mở ${item.symbol} trên TradingView`}>
                <OpenInNewIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.4, mb: 0.15, fontSize: '0.6rem' }}>
          GIÁ HIỆN TẠI
        </Typography>
        <Typography variant="h6" fontWeight={750} sx={{ letterSpacing: -0.25 }}>
          {formatPrice(item.price)}
          {Number.isFinite(Number(item.price)) ? (
            <Typography component="span" color="text.secondary" fontSize="0.72rem" sx={{ ml: 0.6 }}>USDT</Typography>
          ) : null}
        </Typography>
      </CardContent>

      <CardActions sx={{ px: 1.6, pb: 1.4, pt: 0, gap: 0.75, alignItems: 'stretch' }}>
        <Button
          variant="contained"
          disabled={!canOrder || busy}
          onClick={() => onOrder(item.symbol, side)}
          sx={{
            minWidth: 82,
            minHeight: 30,
            height: 34,
            px: 1.4,
            py: 0.4,
            bgcolor: accent,
            color: '#fff',
            fontWeight: 700,
            '&:hover': { bgcolor: accent, filter: 'brightness(1.08)' },
          }}
        >
          NOW
        </Button>
        <Tooltip title="Bỏ theo dõi">
          <span>
            <IconButton
              size="small"
              color="inherit"
              disabled={busy}
              onClick={() => onUnwatch(item.symbol)}
              aria-label={`Bỏ theo dõi ${item.symbol}`}
              sx={{ width: 34, height: 34, ml: 'auto' }}
            >
              <VisibilityOffIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </span>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

function BoxTitle({ symbol }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1" fontWeight={800} letterSpacing={0.1} noWrap title={symbol}>{symbol}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem' }}>USDT PERPETUAL</Typography>
    </Stack>
  );
}
